import store from 'src/store';
import requestErrorParser from '@helpers/requestErrorParser';
import { ApiError } from 'src/errors/ApiError';
import { authService } from 'src/services/authService';
import fetchMetadata from '@api/base/fetchMetadata';
import { logApiError, newRequestId } from '@api/base/apiLogger';
import { isVerboseDebugEnabled } from '@utils/debugMode';

declare const HIDE_NETWORK_LOGS: boolean;

export default <T>(
  client,
  method,
  request = null,
  needHeaders = true,
  showErrorMessage = true
): Promise<T> => {
  return fetchMetadata(needHeaders).then((rawMetadata) => {
    const requestId = newRequestId();
    // Envoy preserves this id (preserve_external_request_id) and the backend
    // propagates it across services, logs it, and echoes it in trailers.
    const metadata = { ...rawMetadata, 'x-request-id': requestId };

    if (isVerboseDebugEnabled()) {
      if (HIDE_NETWORK_LOGS !== true) {
        console.group(`>> ${method.name}`);
        if (request != null) {
          console.info(request.toObject ? request.toObject() : structuredClone(request.array));
        }
        console.groupEnd();
      }
    }

    const startedAt = performance.now();

    return new Promise((resolve, reject) => {
      method.call(client, request, metadata, (err, res) => {
        if (err) {
          logApiError({
            methodName: method.name,
            code: err.code,
            message: err.message,
            requestId,
            startedAt,
            grpcMetadata: err.metadata,
          });

          if (isVerboseDebugEnabled()) {
            if (HIDE_NETWORK_LOGS !== true) {
              console.group(`<< ${method.name}`);
              console.error(err);
              console.groupEnd();
            }
          }

          if (showErrorMessage) {
            store.dispatch(requestErrorParser(err.message, false, requestId));
          }

          // Prevent forced logout for unauthenticated flows (e.g., login with wrong password)
          // `needHeaders === false` is used by auth-related calls like login/register where
          // there is no active session yet. In those cases, avoid redirecting to keep error visible.
          if (err.code === 16 && needHeaders) {
            authService.logout();
          }

          return reject(new ApiError(err.message, err.code, err, requestId));
        }

        if (isVerboseDebugEnabled()) {
          if (HIDE_NETWORK_LOGS !== true) {
            console.group(`<< ${method.name}`);
            console.info(structuredClone(res.toObject()));
            console.groupEnd();
          }
        }
        const jsonRes = res.toObject();
        const jsonErrMessage = jsonRes.errorMessage || jsonRes.message || jsonRes.details;

        if (jsonRes.success === false && jsonErrMessage) {
          logApiError({
            methodName: method.name,
            code: jsonRes.code,
            message: jsonErrMessage,
            requestId,
            startedAt,
          });

          if (showErrorMessage) {
            store.dispatch(requestErrorParser(jsonErrMessage, true, requestId));
          }

          reject(new ApiError(jsonErrMessage, jsonRes.code, jsonRes, requestId));
        }

        return resolve(res.toObject());
      });
    });
  });
};
